import { Platform } from '@angular/cdk/platform';
import { ComponentFactoryResolver, ComponentRef, Injectable, Injector, Type } from '@angular/core';
import { Router } from '@angular/router';
import { NavigationDocument } from '@barthauer/abc/arena';
import {
  AbcCore,
  CompareHelper,
  ConvertHelper,
  DotNetBridge,
  ObjectHelper,
  ProfileCommandVisibility,
  UrlHelper,
} from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  CacheService,
  CacheStorageAdapter,
  CoordinateDef,
  DataService as CoreDataService,
  ExecuteMoveNodeDef,
  ICoreDataEntity,
  NetworkTraceModel,
  ObjectSpaceService,
  ObjectTypeInfoViewModel,
  PersistMode,
  PersistObjectsModel,
  QueryColumn,
  QueryColumnSortOrder,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { ErrorHandlingService } from '@barthauer/abc/core/error-handling';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  ContextMenuConfiguration,
  ContextMenuObjectTypeDefinition,
  DataEntity,
  MapConfiguration,
  MapService,
  QueryMapModel,
} from '@barthauer/abc/graphics/adapters/open-layers';
import { LoadProtocolService, LoadProtocolTaskInfoSnapshot } from '@barthauer/abc/messages';
import { AbcCoreComponent, AbcCoreContainerService } from '@barthauer/abc/ui-components';
import { LoginInfo, ProfileInfo, ProfileService, UserService } from '@barthauer/basys/core/authentication';
import { ArenaService } from 'libs/abc/arena/src/lib/core/arena.service';
import { ArenaCacheSettings } from 'libs/abc/arena/src/lib/core/models/setting.model';
import { NavigationNode, NavigationNodeBase } from 'libs/abc/arena/src/lib/core/navigation/navigation.model';
import { LoginComponentInfo } from 'libs/abc/core/authentication/src/lib/login/login-component-info';
import { WebApiEditorComponentInfo } from 'libs/abc/core/authentication/src/lib/webapi-editor/webapi-editor-component-info';
import { MapSelection } from 'libs/abc/graphics/adapters/open-layers/src/lib/map.selection';
import { concat, firstValueFrom, forkJoin, from, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { catchError, concatMap, map, tap, toArray } from 'rxjs/operators';
import { DocumentsComponent } from './documents/documents.component';
import {
  AdditionalObjectTypeConfig,
  BasysMapFrameConfiguration,
  BasysMapFrameInitConfig,
  CommandConfig,
  RouteConfiguration,
  WidgetConfiguration,
} from './models/config.model';
import {
  CreateGeoObjectInfo,
  MapObjectValue,
  NetworkObjectInfo,
  NetworkTracingInfo,
  ObjectTypeWithGeoDefsCandidate,
  SelectOtAndGidListItem,
} from './models/mapsData.model';
import { ActionSetting, BooleanSetting, Setting } from './models/setting.model';
import { NetworktracingComponent } from './networktracing/networktracing.component';
import { ObjectInfoPanelComponent } from './object-info/object-info-panel/object-info-panel.component';
import { CordovaService } from '@barthauer/abc/core';
import { DataService } from './services/data.service';
import { LibService } from './services/lib.service';
import { RoutingService } from './services/routing.service';
import { TableComponent } from './table/table.component';
import {
  ArenaSettingsViewModel,
  MapInfoObjectTypeViewModel,
  MapInfoViewModel,
  ProfileInfoWithArenaViewModel,
  ProfileSettingsWithArenaViewModel,
} from './viewModels/mapsData.viewModel';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { DashboardComponent } from './dashboard/dashboard.component';

declare let cordova: any;

export interface ObjectTypeWithFilter {
  objectType: MapInfoObjectTypeViewModel;
  filter: string;
  storageKey?: string;
  refresh?: boolean;
}

@Injectable({ providedIn: 'root' })
export class BasysMapFrameService {
  public static readonly Route_Root: string = 'Root';

  public static readonly Widget_Table: string = 'Table';
  public static readonly Widget_ObjectInfo: string = 'ObjectInfo';
  public static readonly Widget_Documents: string = 'Documents';
  public static readonly Widget_Dashboard: string = 'Dashboard';

  public static readonly Setting_CacheConfig: string = 'CacheConfig';
  public static readonly Setting_GeoLocation: string = 'GeoLocation';
  public static readonly Setting_ObjectInfoAsPopup: string = 'ObjectInfoAsPopup';
  public static readonly Setting_AllowMapRotation: string = 'AllowMapRotation';
  public static readonly Setting_Export_DWAM150: string = 'Export_DWAM150';
  public static readonly Setting_Export_Gis: string = 'Export_Gis';
  public static readonly Setting_Import_Ascii: string = 'Import_Ascii';

  public static readonly ContextMenuCommandName_Map_EditObject: string = 'Map_EditObject';
  public static readonly ContextMenuCommandName_Map_DeleteObject: string = 'Map_DeleteObject';
  public static readonly ContextMenuCommandName_Map_MoveNode: string = 'Map_MoveNode';
  public static readonly ContextMenuCommandName_Map_ShowDocumentsList: string = 'Map_ShowDocumentsList';
  public static readonly ContextMenuCommandName_Map_OpenTargetUrl: string = 'Map_OpenTargetUrl';

  public static readonly ContextMenuCommandName_PipeLongitudinalSection: string = 'PipeLongitudinalSection';
  public static readonly ContextMenuCommandName_PipeGraphics: string = 'PipeGraphics';
  public static readonly ContextMenuCommandName_NodeGraphics: string = 'NodeGraphics';
  public static readonly ContextMenuCommandName_NetworkTracing: string = 'NetworkTracing';
  public static OBJECTTYPE_ABWLEITUNG: string = 'AbwLeitung';
  public static OBJECTTYPE_ABWHALTUNGBASE: string = 'AbwHaltungBase';
  public readonly IdColumnName: string = ObjectHelper.nameof<ICoreDataEntity>('Id');
  public readonly ObjectTypeColumnName: string = ObjectHelper.nameof<ICoreDataEntity>('ObjectType');

  public resAsync: Promise<ResourceSet>;
  public componentRef: any;
  public frameConfig: BasysMapFrameConfiguration;
  public map: MapService;
  public mapInfo: MapInfoViewModel;
  public modifyConfig: (config: BasysMapFrameConfiguration) => Promise<BasysMapFrameConfiguration>;
  public widgetInstantiated: (WidgetConfiguration) => void;
  public mapInfoLoaded: (MapInfoViewModel) => void;
  public rootRoute: RouteConfiguration;
  private initSubject: Subject<void> = new Subject<void>();
  public isProfileChanged: boolean;
  public rtlEnabled = false;
  private currentProfile: ProfileInfo;
  private frameConfigSubject = new Subject<BasysMapFrameConfiguration>();
  private bottomSheetClosingSubject = new Subject<void>();
  public bottomSheetClosing$ = this.bottomSheetClosingSubject.asObservable();
  private mainMenuCloseSubject = new Subject<void>();
  public mainMenuClose$ = this.mainMenuCloseSubject.asObservable();
  public isBottomSheetToggleAllowed: () => Promise<boolean> = () => of(true).toPromise();
  private mapInfos = new ReplaySubject<boolean>(1);
  private widgetInstances: Map<WidgetConfiguration, ComponentRef<unknown>> = new Map<
    WidgetConfiguration,
    ComponentRef<unknown>
  >();
  private openDataTableSubject = new Subject<ObjectTypeWithFilter>();
  private openDataTableSelectionSubject = new Subject<DataEntity[]>();
  // the varaiable below is used when we open Opera inside Opera manager
  public isStandalone = true;
  public hasLoggedOut: boolean = false;
  private isLimitedVersion = false;

  public destroyObjectInfo = new Subject();
  public detectChanges: () => void = () => {};
  public initSearchValues: () => Promise<void>;

  private networkTraceContainer: AbcCoreComponent;
  private networkTracePopup: NetworktracingComponent;

  private preselectedDataEntities: DataEntity[];

  //Keep cordovaService for early instantiation
  constructor(
    public cordovaService: CordovaService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private userService: UserService,
    private profileService: ProfileService,
    mapService: MapService,
    private arenaService: ArenaService /* *** Added to initialize arena caching! *** */,
    public data: DataService,
    public coreData: CoreDataService,
    public objectSpace: ObjectSpaceService,
    public cache: CacheService,
    public error: ErrorHandlingService,
    public lib: LibService,
    public config: AbcConfigService,
    public routing: RoutingService,
    public loadingScreenService: LoadProtocolService,
    private resourceService: ResourceManagerService,
    private platform: Platform,
    private router: Router,
    private abcCore: AbcCore,
    private abcCoreContainerService: AbcCoreContainerService,
  ) {
    this.resAsync = resourceService.getResourceSet('basys-map-frame');
    this.map = mapService;
    this.lib.frame = this;

    if (cordovaService.isMobileDeviceReadyDetected) {
      this.map.setIsMobileEnvironment(true);
      this.map.setMobileEnvironment(true); //BehaviourSubject to enable later subscription
    } else {
      cordovaService.onDeviceReady.subscribe(() => {
        this.map.setIsMobileEnvironment(true);
        this.map.setMobileEnvironment(true); //BehaviourSubject to enable later subscription
      });
    }

    this.map.registerMapSelectEntitiesDefaultAction((_) => {
      if (!this.lib.isBottomSheetOpenByName(BasysMapFrameService.Widget_Table)) {
        this.lib.openBottomSheet(this.getWidget(BasysMapFrameService.Widget_Table));
        setTimeout(() => {
          this.openDataTableSelectionSubject.next(_);
        }, 100);
      } else {
        this.openDataTableSelectionSubject.next(_);
      }
    });

    let initMapTask: LoadProtocolTaskInfoSnapshot;
    let loadMapTask: LoadProtocolTaskInfoSnapshot;

    this.initSubject
      .asObservable()
      .pipe(
        concatMap(() => {
          return this.map.onMapInitiated().pipe(
            concatMap(async () => {
              initMapTask = this.loadingScreenService.addNewTask(
                (await this.lib.resAsync).get('MapConfigLoading'),
                true,
              );
              return initMapTask;
            }),
            concatMap(() => {
              return this.getCurrentProfile(true).pipe(
                concatMap((profileInfo) =>
                  from(this.createSettings(profileInfo?.settings)).pipe(
                    concatMap((m) => {
                      return from(this.resAsync).pipe(
                        map((res) => {
                          return { pi: profileInfo, m: m, res: res };
                        }),
                      );
                    }),
                  ),
                ),
                concatMap((result) => {
                  const profileInfo = result.pi;
                  const settings = result.m;
                  const res = result.res;
                  const frameConfig: BasysMapFrameConfiguration = new BasysMapFrameConfiguration();
                  frameConfig.arenaCurrentSettings = profileInfo.settings.arena;
                  frameConfig.globalConfig = config.configuration;
                  frameConfig.mapConfig = new MapConfiguration();
                  frameConfig.mapConfig.WebApiUrl = UrlHelper.buildUrl(frameConfig.globalConfig.WebApi.BaseURL, 'api');
                  frameConfig.mapConfig.ReadMapMessage = new QueryMapModel();
                  frameConfig.mapConfig.ImageVectorScaleLimit = 10000;

                  frameConfig.widgets.push(
                    this.createWidgetConfig(
                      BasysMapFrameService.Widget_Table,
                      TableComponent,
                      res.svgName('TableMenu-Image'),
                    ),
                  );
                  frameConfig.widgets.push(
                    this.createWidgetConfig(
                      BasysMapFrameService.Widget_ObjectInfo,
                      ObjectInfoPanelComponent,
                      res.svgName('ObjectInfoMenu-Image'),
                      () =>
                        !(frameConfig.settings.get(BasysMapFrameService.Setting_ObjectInfoAsPopup) as BooleanSetting)
                          .value,
                    ),
                  );
                  frameConfig.widgets.push(
                    this.createWidgetConfig(BasysMapFrameService.Widget_Documents, DocumentsComponent),
                  );

                  // frameConfig.widgets.push(
                  //   this.createWidgetConfig(
                  //     BasysMapFrameService.Widget_Dashboard,
                  //     DashboardComponent,
                  //     res.svgName('Dashboard-Image'),
                  //   ),
                  // );

                  frameConfig.routes.push(this.rootRoute);

                  frameConfig.settings = settings;

                  frameConfig.contextMenuCommands.set(BasysMapFrameService.ContextMenuCommandName_Map_EditObject, {
                    designation: BasysMapFrameService.ContextMenuCommandName_Map_EditObject,
                    action: (entity) => this.lib.showObjectInfo([entity]),
                  });
                  frameConfig.contextMenuCommands.set(BasysMapFrameService.ContextMenuCommandName_Map_DeleteObject, {
                    designation: BasysMapFrameService.ContextMenuCommandName_Map_DeleteObject,
                    action: (entity) => this.deleteObjects([entity]),
                    isOnlineOnly: true,
                  });
                  frameConfig.contextMenuCommands.set(BasysMapFrameService.ContextMenuCommandName_Map_MoveNode, {
                    designation: BasysMapFrameService.ContextMenuCommandName_Map_MoveNode,
                    action: async (entity) => await this.moveNode(entity),
                    isOnlineOnly: true,
                  });

                  frameConfig.contextMenuCommands.set(
                    BasysMapFrameService.ContextMenuCommandName_Map_ShowDocumentsList,
                    {
                      designation: BasysMapFrameService.ContextMenuCommandName_Map_ShowDocumentsList,
                      action: (entity) => this.lib.showDocuments([entity], false),
                      isOnlineOnly: true,
                    },
                  );

                  if (
                    profileInfo?.settings?.commands?.find(
                      (_) =>
                        _.name === BasysMapFrameService.ContextMenuCommandName_Map_OpenTargetUrl &&
                        _.visibility !== ProfileCommandVisibility.AlwaysHidden,
                    )
                  ) {
                    frameConfig.contextMenuCommands.set(BasysMapFrameService.ContextMenuCommandName_Map_OpenTargetUrl, {
                      designation: BasysMapFrameService.ContextMenuCommandName_Map_OpenTargetUrl,
                      action: (entity) =>
                        this.lib.openTargetUrl([entity], null, (e, n) => Promise.resolve(e.feature?.get(n))),
                      isOnlineOnly: true,
                    });

                    mapService.targetButtonNames.push(BasysMapFrameService.ContextMenuCommandName_Map_OpenTargetUrl);
                  }

                  const configResult = this.modifyConfig ? from(this.modifyConfig(frameConfig)) : of(frameConfig);
                  const arenaCacheSettings: ArenaCacheSettings = new ArenaCacheSettings();
                  arenaCacheSettings.modifyNavigationDocument = async (navDoc: NavigationDocument) => {
                    await this.modifyNavigationDocument(navDoc, profileInfo?.settings?.arena, frameConfig);
                  };
                  this.arenaService.setCacheSettings(arenaCacheSettings);
                  this.rtlEnabled = this.resourceService.isRightToLeftEnabled;
                  return configResult;
                }),
              );
            }),
          );
        }),
      )
      .subscribe(
        async (conf) => {
          this.hasLoggedOut = false;
          if (initMapTask) {
            this.loadingScreenService.finishTask(initMapTask.id);
          }
          loadMapTask = this.loadingScreenService.addNewTask((await this.lib.resAsync).get('MapLoading'), true);
          this.frameConfig = conf;
          this.frameConfig.prepareSettings();
          this.initGeoLocation();
          this.map.loadMapConfig(conf.mapConfig);
          this.routing.routes = conf.routes;
          if (!CacheStorageAdapter.forceOfflineMode) {
            await this.tryReadClientOrServerStoredNumber(
              'resolutionValue',
              (val) => (this.cordovaService.pictureResolution = val),
            );
            await this.tryReadClientOrServerStoredNumber(
              'qualityValue',
              (val) => (this.cordovaService.pictureQuality = val),
            );
          }
          this.frameConfigSubject.next(conf);
        },
        (err) => {
          console.error(err);
          if (initMapTask) {
            this.loadingScreenService.finishTask(initMapTask.id);
          }
          if (loadMapTask) {
            this.loadingScreenService.finishTask(loadMapTask.id);
          }
        },
      );

    this.map.onMapLoaded().subscribe(
      (mapSelection) => {
        setTimeout(async () => {
          if (
            mapSelection &&
            this.frameConfig?.settings &&
            mapService?.targetButtonNames.find(
              (_) => _ === BasysMapFrameService.ContextMenuCommandName_Map_OpenTargetUrl,
            )
          ) {
            let added = false;
            let lastSetting: ActionSetting;
            for (const ly of mapSelection.getAllMapLayerDefinitions()) {
              if (ly?.target?.url) {
                const parts = ly.target.url.split('|');
                const key = 'OpenTargetUrl:' + parts[0];
                if (parts.length > 1 && parts[1] && !this.frameConfig.settings.get(key)) {
                  const name = ly.target.name ?? parts[0];
                  added = true;
                  this.frameConfig.settings.set(
                    key,
                    (lastSetting = new ActionSetting({
                      designation: name,
                      caption: (await this.lib.resAsync).get('Setting-OpenArena', name),
                      action: () => this.lib.openUrl(parts[0], ly.target.name, parts.length > 2 ? parts[2] : undefined),
                      svgIconName: (await this.lib.resAsync).svgName('TargetUrlOpen-Image'),
                    })),
                  );
                }
              }
            }
            if (added) {
              lastSetting.beginGroupBehind = true;
              this.frameConfig.prepareSettings();
              this.frameConfigSubject.next(this.frameConfig);
            }
          }
          this.queryMapInfo(
            undefined,
            undefined,
            this.frameConfig.additionalObjectTypeConfigs
              ? this.frameConfig.additionalObjectTypeConfigs.map((c) => c.objectTypeName)
              : undefined,
          ).subscribe(
            (result) => {
              this.mapInfo = result;
              this.mapInfo.initialize();
              this.data.setMapInfo(this.mapInfo, (ot, ctx) => {
                const parameters: ParameterValue[] = [];
                return this.queryMapObjects(
                  ot,
                  mapSelection.getMapQueryCache()?.buildExtentFilterFromContext(ctx, parameters),
                  parameters,
                );
              });

              this.map.loadContextMenuConfig(this.createContextMenuConfiguration(this.mapInfo));
              this.mapInfos.next(true);

              const canAddPoints = this.mapInfo.objectTypes.find((_) => _.points.find((g) => g.canAdd) != null) != null;
              const canAddLines = this.mapInfo.objectTypes.find((_) => _.lines.find((g) => g.canAdd) != null) != null;
              const canAddPolygons =
                this.mapInfo.objectTypes.find((_) => _.polygons.find((g) => g.canAdd) != null) != null;
              this.map.supportsCreateGeometry(canAddPoints, canAddLines, canAddPolygons);

              if (this.mapInfoLoaded) {
                this.mapInfoLoaded(this.mapInfo);
              }
              this.map.startSearchingGeoLocationDevice();

              if (loadMapTask) {
                this.loadingScreenService.finishTask(loadMapTask.id);
              }

              const cacheSettings = this.arenaService.getCacheSettings();
              if (cacheSettings) {
                cacheSettings.applyExtentFilter = async (query, ctx) => {
                  if (query?.ObjectQueries?.length) {
                    let ot = await this.objectSpace.getObjectType(query.ObjectQueries[0].ObjectType).toPromise();
                    while (ot) {
                      if (ot.columnsByName.has('XmlGeometries')) {
                        return mapSelection.getMapQueryCache()?.applyExtentFilterFromContext(ctx, query);
                      }
                      ot = ot.baseType;
                    }
                  }
                  return query;
                };
              }

              if (this.initSearchValues) {
                setTimeout(() => {
                  if (this.initSearchValues) {
                    this.initSearchValues();
                  }
                }, 1000);
              }

              //TODO: Use UI to save MapImagePreview
              if (mapSelection) {
                let lastViewCenter: number[] = this.map?.getCurrentView()?.getCurrentCenter();
                let lastViewScale: number = this.map?.getCurrentView()?.getCurrentScale();
                let timerId: any;
                timerId = setInterval(() => {
                  const center: number[] = this.map?.getCurrentView()?.getCurrentCenter();
                  const scale: number = this.map?.getCurrentView()?.getCurrentScale();
                  if (scale === lastViewScale && CompareHelper.areDeepEqual(lastViewCenter, center)) {
                    clearInterval(timerId);
                    this.trySaveProfileMapImagePreview(mapSelection);
                  } else {
                    lastViewCenter = center;
                    lastViewScale = scale;
                  }
                }, 10000);
              }
            },
            (error) => {
              this.error.publish(error);
              if (initMapTask) {
                this.loadingScreenService.finishTask(initMapTask.id);
              }
              if (loadMapTask) {
                this.loadingScreenService.finishTask(loadMapTask.id);
              }
            },
          );

          if (this.preselectedDataEntities) {
            this.map.navigateToObjects(this.preselectedDataEntities);
          }
        }, 2000);
      },
      (error) => {
        this.error.publish(error);
        if (initMapTask) {
          this.loadingScreenService.finishTask(initMapTask.id);
        }
        if (loadMapTask) {
          this.loadingScreenService.finishTask(loadMapTask.id);
        }
      },
    );

    this.map.onMapOpenDataTable().subscribe(
      function (node) {
        const template: string = node?.layer?.layerdefinition?.templateName ?? node?.layer?.layerdefinition?.fullName;
        if (this.mapInfo != null) {
          const objectTypeInfo = this.mapInfo.objectTypes.find((_) => _.isMatch(template));
          if (objectTypeInfo) {
            this.openDataTable(objectTypeInfo, node?.layer?.layerdefinition?.templateFilter);
          }
        }
      }.bind(this),
    );

    this.map.canOpenDataTable = (name) => {
      return this.mapInfo?.objectTypes.find((_) => _.isMatch(name)) != null;
    };
    this.destroyObjectInfo.asObservable().subscribe(() => {
      this.lib.ensureClosePopupObjectInfo();
      this.destroyWidgetComponents();
    });
  }

  private async tryReadClientOrServerStoredNumber(key: string, onSuccess: (val: number) => void): Promise<void> {
    let result: number;
    const localStorageValue: string = localStorage.getItem(key);
    if (!CompareHelper.isNullorEmpty(localStorageValue)) {
      result = <number>ConvertHelper.fromString(Number, localStorageValue);
    } else {
      result = await this.coreData
        .executeGetValueQuery({ Name: key })
        .pipe(map((value) => <number>ConvertHelper.fromString(Number, value?.toString())))
        .toPromise();
    }
    if (result) {
      onSuccess(result);
    }
  }

  private onOpenNetworkTrackingPopup(networkTraceInfo: NetworkTracingInfo): void {
    if (!this.networkTraceContainer) {
      this.networkTraceContainer = this.abcCoreContainerService.createComponent(
        this.networkTraceContainer,
        NetworktracingComponent,
      );
    }

    this.networkTracePopup = this.networkTraceContainer.componentRef.instance as NetworktracingComponent;
    this.networkTracePopup.init(networkTraceInfo);
    this.networkTracePopup.onDialogClosed.subscribe((tracingInfo) => this.selectSubnetEntities(tracingInfo));
    this.networkTracePopup.onDialogCancelled.subscribe(() => this.selectSubnetEntitiesCancelled());
  }

  private async modifyNavigationDocument(
    navDoc: NavigationDocument,
    arenaSettings: ArenaSettingsViewModel,
    frameConfig: BasysMapFrameConfiguration,
  ): Promise<void> {
    if (navDoc) {
      if (arenaSettings && arenaSettings.layouts) {
        const allowedObjectTypes = arenaSettings.layouts.map((x) => x.objectTypeName);
        const removeByObjectTypePredicate = (node) =>
          node.Layout && !allowedObjectTypes.some((ot) => ot === node.Layout.ObjectType);
        const removeByChildNodePredicate = (node: NavigationNodeBase) => node.isGroupOnlyNode;
        const ensureAdditionalObjectTypeFilter = (node: NavigationNodeBase) => {
          if (node instanceof NavigationNode) {
            this.ensureAdditionalObjectTypeFilter(node, frameConfig);
          }
        };
        for (let index = 0; index < navDoc.Groups.length; index++) {
          const group = navDoc.Groups[index];
          this.reduceNavigationNodes(group, removeByObjectTypePredicate, null);
          this.reduceNavigationNodes(group, removeByChildNodePredicate, ensureAdditionalObjectTypeFilter);
          if (group.Nodes.length === 0) {
            navDoc.Groups.splice(index, 1);
            index--;
          }
        }
      } else {
        navDoc.Groups.splice(0);
      }
    }
  }

  private ensureAdditionalObjectTypeFilter(node: NavigationNode, frameConfig: BasysMapFrameConfiguration): void {
    if (frameConfig.additionalObjectTypeConfigs.length > 0 && node.Layout) {
      const addConfigs: AdditionalObjectTypeConfig[] = frameConfig.additionalObjectTypeConfigs.filter(
        (x) => x.objectTypeName === node.Layout.ObjectType,
      );
      if (addConfigs.length > 0) {
        //assuming only one config per objecttype
        const addConfig: AdditionalObjectTypeConfig = addConfigs[0];
        if (addConfig.oPathFilter) {
          let addFilter = addConfig.oPathFilter;
          for (const param of addConfig.filterParameters) {
            //lazy: no type checking
            addFilter = addFilter.replace('$' + param.Name, "'" + param.Value + "'");
          }
          node.Filter = node.Filter ? '(' + node.Filter + ') and (' + addFilter + ')' : addFilter;
        }
      }
    }
  }

  private reduceNavigationNodes(
    parentNode: NavigationNodeBase,
    removePredicate: (node: NavigationNodeBase) => boolean,
    modifyMethod: (node: NavigationNodeBase) => void,
  ): void {
    if (parentNode.Nodes) {
      for (let index = 0; index < parentNode.Nodes.length; index++) {
        const node = parentNode.Nodes[index];
        if (removePredicate(node)) {
          parentNode.Nodes.splice(index, 1);
          index--;
        } else {
          if (modifyMethod) {
            modifyMethod(node);
          }
          this.reduceNavigationNodes(node, removePredicate, modifyMethod);
        }
      }
    }
  }

  async init(initialConfig?: BasysMapFrameInitConfig) {
    const isDotNetStandalone: boolean = await this.isDotNetStandalone();
    const isMobilePlatform: boolean =
      this.platform.ANDROID || this.platform.IOS || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAppLike = isDotNetStandalone || isMobilePlatform;
    try {
      if (!isAppLike) {
        this.ensureServerAvailable();
      }
      this.config.isAppLike = isAppLike;
    } catch {
      this.config.isAppLike = isAppLike;
      this.ensureServerAvailable();
    }

    this.rootRoute = this.createRouteConfig(
      BasysMapFrameService.Route_Root,
      initialConfig?.mapsPath || '',
      initialConfig?.mapsOutlet,
    );
    this.initSubject.next(null);
    this.isStandalone = initialConfig?.isStandalone ?? true;
    this.isLimitedVersion = initialConfig?.isLimitedVersion ?? false;

    if (initialConfig?.preselectedDataEntities) {
      this.preselectedDataEntities = initialConfig?.preselectedDataEntities;
    }
  }

  private async isDotNetStandalone(): Promise<boolean> {
    return DotNetBridge.hasSettings && (await DotNetBridge.settings.get('IsStandalone'));
  }

  public canChangeProfileOrLogout(): boolean {
    return (
      !CacheStorageAdapter.forceOfflineMode && (CacheStorageAdapter.isOnline || !CacheStorageAdapter.isDownloadComplete)
    );
  }

  public canChangeContext(): boolean {
    if (
      CacheStorageAdapter.forceOfflineMode ||
      (!CacheStorageAdapter.isOnline && CacheStorageAdapter.isDownloadComplete)
    ) {
      return true;
    } else {
      return CacheStorageAdapter.isOnline;
    }
  }

  ensureServerAvailable(): void {
    this.cache.storage.onServerUnavailable(() => {
      this.lib.resAsync.then((resset) => alert(resset.get('ServerError')));
      if (
        !LoginComponentInfo.isCurrentPageLoginPage(this.router) &&
        !WebApiEditorComponentInfo.isCurrentPageWebApiEditorPage(this.router)
      ) {
        this.userService.navigateToLoginPage();
      }
    });
  }

  getWidget(designation: string) {
    return this.frameConfig.widgets.find((w) => w.designation === designation);
  }

  refreshWidgets() {
    this.frameConfigSubject.next(this.frameConfig);
  }
  createWidgetConfig(
    designation: string,
    componentType: Type<unknown>,
    svgIconName?: string,
    visibleCondition?: () => boolean,
    badgeValueExpression?: () => string,
    route?: RouteConfiguration,
  ): WidgetConfiguration {
    const wc: WidgetConfiguration = new WidgetConfiguration();

    wc.designation = designation;
    wc.componentType = componentType;
    wc.svgIconName = svgIconName;
    wc.visibleCondition = visibleCondition;
    wc.badgeValueExpression = badgeValueExpression;
    wc.route = route;
    return wc;
  }

  createRouteConfig(designation: string, path: string, outlet?: string): RouteConfiguration {
    const route = new RouteConfiguration();
    route.designation = designation;
    route.path = path;
    route.outlet = outlet;
    return route;
  }

  ensureWidgetComponent(widgetConfig: WidgetConfiguration): ComponentRef<unknown> {
    let component = this.widgetInstances.get(widgetConfig);
    if (!component || component.hostView.destroyed) {
      component = this.loadComponent(widgetConfig.componentType);
      this.widgetInstances.set(widgetConfig, component);
      if (this.widgetInstantiated) {
        this.widgetInstantiated(widgetConfig);
      }
    }
    return this.widgetInstances.get(widgetConfig);
  }

  private destroyWidgetComponents(): void {
    this.widgetInstances.forEach((v) => v.destroy());
  }

  loadComponent(component: Type<unknown>, viewContainer?: any): ComponentRef<unknown> {
    const componentRef = this.componentFactoryResolver.resolveComponentFactory(component).create(this.injector);

    if (viewContainer) {
      viewContainer.insert(componentRef.hostView);
    }

    return componentRef;
  }

  getLoginInfo(doRefresh?: boolean): Observable<LoginInfo> {
    return this.userService.getCurrentLoginInfo(doRefresh);
  }

  logout(removeCurrentProfile: boolean = false): Observable<boolean> {
    this.hasLoggedOut = true;
    this.lib.closeGraphicsPopups();
    if (removeCurrentProfile) {
      return this.profileService.removeCurrentProfile().pipe(concatMap(() => this.userService.logoutCurrentUser(true)));
    } else {
      return this.userService.logoutCurrentUser(true);
    }
  }

  get areProfilesSupported(): boolean {
    return this.profileService.areProfilesSupported();
  }

  getCurrentProfile(withSettings: boolean = false): Observable<ProfileInfoWithArenaViewModel> {
    return this.data.getCurrentProfile(this, withSettings).pipe(
      tap((currentProfile) => {
        if (this.currentProfile?.id !== currentProfile?.id) {
          this.isProfileChanged = true;
        }
      }),
    );
  }

  getCurrentConfig(): Observable<BasysMapFrameConfiguration> {
    return this.frameConfigSubject.asObservable();
  }

  selectProfile(forwardTo?: string): Observable<boolean> {
    return from(this.profileService.navigateToProfileSelectionPage(null, null, forwardTo));
  }

  queryMapInfo(
    packageId?: string,
    mapNames?: string[],
    additionalObjectTypeNames?: string[],
  ): Observable<MapInfoViewModel> {
    return this.data.getMapInfo(packageId, mapNames, additionalObjectTypeNames).pipe(
      concatMap((data) => {
        const mapInfo = new MapInfoViewModel(data);
        const obs: Observable<ObjectTypeInfoViewModel>[] = new Array<Observable<ObjectTypeInfoViewModel>>();

        for (const mapInfoObjectType of mapInfo.objectTypes) {
          obs.push(
            this.objectSpace.getObjectType(mapInfoObjectType.name).pipe(
              tap((otvm) => {
                mapInfoObjectType.objectType = otvm;
              }),
            ),
          );
        }
        return concat(...obs)
          .pipe(toArray())
          .pipe(map((x) => mapInfo));
      }),
    );
  }

  queryMapObjectValues(
    queryText: string,
    objectTypeName?: string,
    packageId?: string,
    mapNames?: string[],
  ): Observable<MapObjectValue[]> {
    return this.data.getMapObjectValues(queryText, objectTypeName, packageId, mapNames);
  }

  private initGeoLocation(): void {
    const geoLocSetting = this.frameConfig.settings.get(BasysMapFrameService.Setting_GeoLocation) as BooleanSetting;
    if (geoLocSetting.visible) {
      this.map.onGeoLocationStateChanged().subscribe(
        (state) => {
          geoLocSetting.enabled = state.deviceAvailable;
          if (state.deviceAvailable && geoLocSetting.value === undefined) {
            geoLocSetting.value = false; //default!
          }
        },
        (error) => console.log(error),
      );
    }
  }

  private async createSettings(profileSettings: ProfileSettingsWithArenaViewModel): Promise<Map<string, Setting>> {
    const settings = new Map<string, Setting>();
    settings.set(
      BasysMapFrameService.Setting_CacheConfig,
      new ActionSetting({
        designation: BasysMapFrameService.Setting_CacheConfig,
        sortOrder: 0,
        caption: (await this.lib.resAsync).get('CacheConfig-Title'),
        svgIconName: (await this.lib.resAsync).svgName('Download-Image'),
        action: () => this.lib.showCacheConfigComponent(),
        beginGroupBehind: true,
      }),
    );

    if (profileSettings?.hasVisibleCommand(BasysMapFrameService.Setting_Export_DWAM150)) {
      settings.set(
        BasysMapFrameService.Setting_Export_DWAM150,
        new ActionSetting({
          designation: BasysMapFrameService.Setting_Export_DWAM150,
          caption: (await this.lib.resAsync).get('Setting-Export-DWAM150'),
          svgIconName: (await this.lib.resAsync).svgName('Download-Image'),
          action: () => this.lib.showM150ExportComponent(),
          beginGroupBehind: !profileSettings.hasVisibleCommand(BasysMapFrameService.Setting_Export_Gis),
          groupName: (await this.lib.resAsync).get('MenuGroupCaption-Export'),
        }),
      );
    }

    if (profileSettings?.hasVisibleCommand(BasysMapFrameService.Setting_Export_Gis)) {
      settings.set(
        BasysMapFrameService.Setting_Export_Gis,
        new ActionSetting({
          designation: BasysMapFrameService.Setting_Export_Gis,
          caption: (await this.lib.resAsync).get('Setting-Export-Gis'),
          svgIconName: (await this.lib.resAsync).svgName('Download-Image'),
          action: () => this.lib.showGisExportComponent(),
          beginGroupBehind: true,
          groupName: (await this.lib.resAsync).get('MenuGroupCaption-Export'),
        }),
      );
    }

    if (profileSettings?.hasVisibleCommand(BasysMapFrameService.Setting_Import_Ascii)) {
      const importAsciiActionSetting = new ActionSetting({
        designation: BasysMapFrameService.Setting_Import_Ascii,
        caption: (await this.lib.resAsync).get('Setting_Import_Shape'),
        svgIconName: (await this.lib.resAsync).svgName('DocumentsOpen-Image'), //#Shape icon to be changed
        action: () => this.lib.showShapeImportComponent(),
        beginGroupBehind: true,
        groupName: (await this.lib.resAsync).get('MenuGroupCaption-Import'),
      });

      CacheStorageAdapter.isDataChanged.subscribe(async () => {
        importAsciiActionSetting.enabled = CacheStorageAdapter.isOnline;
      });

      settings.set(BasysMapFrameService.Setting_Import_Ascii, importAsciiActionSetting);
    }

    const geoLocationSetting = new BooleanSetting({
      designation: BasysMapFrameService.Setting_GeoLocation,
      sortOrder: 3,
      caption: (await this.lib.resAsync).get('Setting-GeoLocation'),
      enabled: false,
    });
    geoLocationSetting.enabledChanged = (enabled) => {
      if (enabled) {
        geoLocationSetting.value = ConvertHelper.toBoolean(
          localStorage.getItem(BasysMapFrameService.Setting_GeoLocation),
        );
      }
    };
    geoLocationSetting.valueChanged = (val) => {
      if (geoLocationSetting.enabled) {
        if (val) {
          this.map.startGeoLocationTracking();
        } else {
          this.map.stopGeoLocationTracking();
        }
        localStorage.setItem(BasysMapFrameService.Setting_GeoLocation, ConvertHelper.toString(val));
      }
    };

    settings.set(BasysMapFrameService.Setting_GeoLocation, geoLocationSetting);
    const rotationEnabled = ConvertHelper.toBoolean(
      localStorage.getItem(BasysMapFrameService.Setting_AllowMapRotation) ?? 'false',
    );
    this.map.setRotationEnabled(rotationEnabled);
    settings.set(
      BasysMapFrameService.Setting_AllowMapRotation,
      new BooleanSetting({
        designation: BasysMapFrameService.Setting_AllowMapRotation,
        sortOrder: 5,
        caption: (await this.lib.resAsync).get('Setting-AllowMapRotation'),
        enabled: true,
        value: rotationEnabled,
        valueChanged: (val) => {
          localStorage.setItem(BasysMapFrameService.Setting_AllowMapRotation, ConvertHelper.toString(val));
          this.map.setRotationEnabled(val);
        },
      }),
    );
    settings.set(
      BasysMapFrameService.Setting_ObjectInfoAsPopup,
      new BooleanSetting({
        designation: BasysMapFrameService.Setting_ObjectInfoAsPopup,
        sortOrder: 6,
        caption: (await this.lib.resAsync).get('Setting-ObjectInfoAsPopup'),
        enabled: true,
        value: ConvertHelper.toBoolean(localStorage.getItem(BasysMapFrameService.Setting_ObjectInfoAsPopup) ?? 'true'),
        beginGroupBehind: true,
        valueChanged: (val) => {
          localStorage.setItem(BasysMapFrameService.Setting_ObjectInfoAsPopup, ConvertHelper.toString(val));
          this.onObjectInfoShowOptionChanged(val);
        },
      }),
    );

    let i = 0;
    const documents = profileSettings?.findSpecialCommandDocuments('Arena');
    if (documents) {
      for (const ad of documents) {
        settings.set(
          'OpenArena:' + ad.name,
          new ActionSetting({
            designation: 'OpenArena:' + ad.name,
            caption: (await this.lib.resAsync).get('Setting-OpenArena', ad.name),
            action: () =>
              this.abcCore.openWindow(
                this.abcCore.buildUrl(this.config.configuration.WebApi.BaseURL, 'arena', ad.name, '/'),
              ),
            svgIconName: (await this.lib.resAsync).svgName('ArenaOpen-Image'),
            beginGroupBehind: ++i == documents.length,
          }),
        );
      }
    }
    return settings;
  }

  onObjectInfoShowOptionChanged(showAsPopup: boolean) {
    if (showAsPopup) {
      this.destroyObjectInfoWidget();
    }
  }

  private destroyObjectInfoWidget(): void {
    this.widgetInstances.forEach((v) => {
      if (v.componentType === ObjectInfoPanelComponent) v.destroy();
    });
  }

  getMapInfosStatus(): Observable<boolean> {
    return this.mapInfos;
  }

  private createContextMenuConfiguration(mapinfo: MapInfoViewModel): ContextMenuConfiguration {
    const ret: ContextMenuConfiguration = new ContextMenuConfiguration();

    if (mapinfo) {
      for (const ot of mapinfo.objectTypes) {
        const commands = ot.commands.filter((_) => _.visibility !== 'AlwaysHidden');
        if (commands.length > 0) {
          const cfg = new ContextMenuObjectTypeDefinition();
          cfg.objectTypes = [ot.name];
          cfg.buttons = [];

          for (const command of commands) {
            let action = null;
            let isOnlineOnlyCommand = command.inputType === 'Sequence' && !CacheStorageAdapter.isDownloadComplete;
            const commandConfig: CommandConfig = this.frameConfig.contextMenuCommands.get(command.name);
            if (commandConfig) {
              if (commandConfig.isOnlineOnly) {
                isOnlineOnlyCommand = true;
              }
              if (!commandConfig.isHidden || commandConfig.isHidden() !== true) {
                action = commandConfig.action;
              }
            } else if (
              command.urlPattern ||
              command.name.endsWith(BasysMapFrameService.ContextMenuCommandName_NetworkTracing)
            ) {
              isOnlineOnlyCommand = true;
              const commandName = command.name;
              const startIndexLongitudenalSection =
                commandName.length - BasysMapFrameService.ContextMenuCommandName_PipeLongitudinalSection.length;
              const isLongitudinalSection =
                commandName.slice(startIndexLongitudenalSection) ===
                BasysMapFrameService.ContextMenuCommandName_PipeLongitudinalSection;

              const startIndexPipeGraphics =
                commandName.length - BasysMapFrameService.ContextMenuCommandName_PipeGraphics.length;
              const isPipeGraphics =
                commandName.slice(startIndexPipeGraphics) === BasysMapFrameService.ContextMenuCommandName_PipeGraphics;

              const startIndexNodeGraphics =
                commandName.length - BasysMapFrameService.ContextMenuCommandName_NodeGraphics.length;
              const isNodeGraphics =
                commandName.slice(startIndexNodeGraphics) === BasysMapFrameService.ContextMenuCommandName_NodeGraphics;

              const startIndexNetworkTracing =
                commandName.length - BasysMapFrameService.ContextMenuCommandName_NetworkTracing.length;
              const isNetworkTracing =
                commandName.slice(startIndexNetworkTracing) ===
                BasysMapFrameService.ContextMenuCommandName_NetworkTracing;

              if (isLongitudinalSection && command.inputType === 'Sequence') {
                action = async (entity) => {
                  await firstValueFrom(this.map.saveLegendState());
                  this.processSequenceSelection(entity, false, (entities) => this.openSequenceResultWindow(entities));
                };
              } else if (isPipeGraphics) {
                action = async (entity: DataEntity) => {
                  await this.lib.openPipeGraphicsComponent(entity);
                };
              } else if (isNodeGraphics) {
                action = async (entity: DataEntity) => {
                  await this.lib.openNodeGraphicsComponent(entity);
                };
              } else if (isNetworkTracing) {
                action = (entity: DataEntity) => {
                  let networkTraceInfo = new NetworkTracingInfo();
                  networkTraceInfo.InitialEntity = entity;
                  networkTraceInfo.InitialObjectType = ot;
                  this.onOpenNetworkTrackingPopup(networkTraceInfo);
                };
              } else {
                action = (entity) => {
                  const getLogindata = forkJoin([
                    this.userService.getCurrentFreshLoginSettings(),
                    this.profileService.getCurrentProfileSettings(),
                  ]);
                  getLogindata.subscribe((settings) => {
                    const url = command.urlPattern
                      .replace('{access_token}', settings[0]?.access_token)
                      .replace('{profile_id}', settings[1]?.id)
                      .replace('{id}', entity.id)
                      .replace('{objecttype}', entity.objectType as string)
                      .replace('{sequence}', entity.id); //TODO: Sequence
                    this.abcCore.openWindow(url);
                  });
                };
              }
            }

            if (action) {
              let badgeExp: (entity: DataEntity) => Promise<number>;
              if (command.badgeValueExpression) {
                badgeExp = (entity: DataEntity) => {
                  if (CacheStorageAdapter.useOfflineMode) {
                    return Promise.resolve(null);
                  }
                  return this.coreData
                    .queryNumber(
                      ot.name,
                      entity.id,
                      command.badgeValueExpression,
                      ...(commandConfig?.badgeValueExpressionParameters
                        ? commandConfig?.badgeValueExpressionParameters
                        : []),
                    )
                    .toPromise();
                };
              }
              let enabledExp: (entity: DataEntity) => Promise<boolean>;
              if (command.enabledExpression || isOnlineOnlyCommand) {
                enabledExp = (entity: DataEntity) => {
                  if (isOnlineOnlyCommand && CacheStorageAdapter.useOfflineMode) {
                    return Promise.resolve(false);
                  }
                  if (!command.enabledExpression) {
                    return Promise.resolve(!command.enabledByBadgeValue);
                  }
                  return this.coreData
                    .queryNumber(
                      ot.name,
                      entity.id,
                      command.enabledExpression,
                      ...(commandConfig?.enabledExpressionParameters ? commandConfig?.enabledExpressionParameters : []),
                    )
                    .toPromise()
                    .then((_) => {
                      if (ConvertHelper.toBoolean(_)) {
                        return true;
                      } else if (CompareHelper.isNullorEmpty(_) || CompareHelper.isEmptyObject(_)) {
                        return null;
                      } else {
                        return false;
                      }
                    });
                };
              }

              if (DotNetBridge.hasCommandHandler) {
                const origaction = action;
                action = async (entity: DataEntity) => {
                  if (command.inputType === 'Sequence') {
                    this.processSequenceSelection(entity, false, async (entities: DataEntity[]) => {
                      const sequence: string = entities
                        .map((e) => e.id + '|' + e.objectType + '|' + e.isInFlowDirection)
                        .join(',');
                      const result: boolean = await DotNetBridge.commandHandler.invokeCommandSequence(
                        command.name,
                        sequence,
                      );
                      if (!result) {
                        return await origaction(entity);
                      }
                    });
                  } else {
                    const result: boolean = await DotNetBridge.commandHandler.invokeCommandSingle(
                      command.name,
                      entity.objectType,
                      entity.id,
                    );
                    if (!result) {
                      return await origaction(entity);
                    }
                  }
                };
              }

              if (!this.isLimitedVersion || !command.urlPattern) {
                cfg.buttons.push({
                  name: command.name,
                  action: action,
                  enabled: true,
                  svgImage: this.getSvgUrl(command.svgImage),
                  tooltip: '',
                  visible: true,
                  badgeHidden: true,
                  badgeValue: 0,
                  badgeValueExpression: badgeExp,
                  enabledByBadgeValue: command.enabledByBadgeValue,
                  enabledCondition: enabledExp,
                });
              }
            }
          }
          if (cfg.buttons.length) ret.definitions.push(cfg);
        }
      }
    }

    return ret;
  }

  private selectSubnetEntitiesCancelled() {
    this.networkTracePopup = undefined;
    this.networkTraceContainer = undefined;
  }

  private selectSubnetEntities(networkTraceInfo: NetworkTracingInfo) {
    {
      const ot = networkTraceInfo.InitialObjectType;
      this.objectSpace
        .getObjectType(ot.name)
        .pipe(
          concatMap((objectType) => {
            const model = networkTraceInfo.TracingModel;
            //const modifiedObjectType = this.applyObjectTypeFilter(objectType, model.IncludeConnectionPipes);

            return this.coreData
              .querySubnet(model)
              .pipe(map((arr) => arr.map((o) => new DataEntity(o.ObjectTypeName, o.Id))));
          }),
        )
        .subscribe(
          (dataEntities) => {
            this.map.navigateToObjects(dataEntities);
          },
          (err) => {
            this.error.publish(err);
          },
        );
    }
  }

  private applyObjectTypeFilter(
    objectType: ObjectTypeInfoViewModel,
    includingConnectionPipes: boolean | undefined = undefined,
  ): string {
    let retObjType = objectType.mainType.name;
    if (this.isWasteWater(objectType)) {
      if (includingConnectionPipes !== undefined && includingConnectionPipes === false) {
        retObjType = BasysMapFrameService.OBJECTTYPE_ABWHALTUNGBASE;
      }
    }

    return retObjType;
  }

  private isWasteWater(objectType: ObjectTypeInfoViewModel) {
    let retWW = false;
    if (objectType && objectType.mainType.name === BasysMapFrameService.OBJECTTYPE_ABWLEITUNG) {
      retWW = true;
    }
    return retWW;
  }

  public processSequenceSelection(
    startObject: DataEntity,
    includeNodes: boolean,
    afterSelectionMethod: (entities: DataEntity[]) => void,
  ) {
    this.map.selectNetworkPath(startObject, includeNodes);

    const subscription: Subscription = this.map.onNetworkPathSelected().subscribe((dataEntities) => {
      afterSelectionMethod(dataEntities);
      subscription?.unsubscribe();
    });
  }

  private async openSequenceResultWindow(entities: DataEntity[]): Promise<void> {
    if (entities && entities.length > 0) {
      await this.lib.openLongitudinalSectionComponent(entities);
    } else {
      this.map.sendMapShortMessage((await this.lib.resAsync).get('NoObjectFoundError'));
    }
  }

  private getSvgUrl(url: string): string {
    return url;
  }

  public openDataTable(objectTypeInfo: MapInfoObjectTypeViewModel, filter: string) {
    this.openDataTableSubject.next({
      objectType: objectTypeInfo,
      filter: filter,
    });
  }

  public onOpenDataTable(): Observable<ObjectTypeWithFilter> {
    return this.openDataTableSubject.asObservable();
  }

  public onOpenDataTableSelection(): Observable<DataEntity[]> {
    return this.openDataTableSelectionSubject.asObservable();
  }

  public getMapObjects(objectType: MapInfoObjectTypeViewModel, filter: string): Observable<any> {
    if (CacheStorageAdapter.useOfflineMode) {
      return this.getMapObjectsFromCache(objectType, filter);
    } else {
      return this.queryMapObjects(objectType, filter).pipe(
        catchError((_) => {
          return this.getMapObjectsFromCache(objectType, filter);
        }),
      );
    }
  }

  private getMapObjectsFromCache(objectType: MapInfoObjectTypeViewModel, filter: string): Observable<any> {
    return from(this.data.tryLoadMapObjects(objectType, filter));
  }

  private queryMapObjects(
    objectType: MapInfoObjectTypeViewModel,
    filter: string,
    filterParameters?: ParameterValue[],
  ): Observable<any> {
    const addColumns: QueryColumn[] = new Array<QueryColumn>();
    let additionalOPath: string;
    let parameters: ParameterValue[];
    if (objectType.objectType) {
      addColumns.push(this.data.createQueryColumn('Count(Documents)', 'ExistsDocuments'));
      if (this.map?.getCurrentQueryParameters) {
        parameters = this.map?.getCurrentQueryParameters();
      }
      const addConf: AdditionalObjectTypeConfig = this.getAdditionalObjectTypeConfig(objectType.name);
      if (addConf) {
        additionalOPath = addConf.oPathFilter;
        parameters = addConf.filterParameters;
        addColumns.push(...addConf.additionalColumns);
        if (filterParameters) {
          //HACK: Remove extent filter if downloading for cache
          filterParameters = null;
          filter = null;
        }
      }
    }
    if (filterParameters) {
      if (!parameters) {
        parameters = [];
      }
      parameters.push(...filterParameters);
    }

    if (filter && filter !== '') {
      if (additionalOPath && additionalOPath !== '') {
        additionalOPath = '(' + additionalOPath + ') And (' + filter + ')';
      } else {
        additionalOPath = filter;
      }
    }
    return this.data.getMapObjects(
      objectType.name,
      objectType.objectSetName,
      objectType.dataSourceId,
      undefined,
      undefined,
      addColumns,
      additionalOPath,
      parameters,
    );
  }

  public getAdditionalObjectTypeConfig(objectTypeName: string): AdditionalObjectTypeConfig {
    return this.frameConfig.additionalObjectTypeConfigs.find((c) => c.objectTypeName === objectTypeName);
  }

  private trySaveProfileMapImagePreview(mapSelection: MapSelection): void {
    if (DotNetBridge.hasContext) {
      return;
    }
    this.userService.getCurrentLoginInfo().subscribe((user) => {
      if (user?.isAdmin) {
        this.profileService.getCurrentProfile().subscribe((profile) => {
          if (profile && !profile?.mapImagePreview && profile?.id != 'self') {
            mapSelection.getMapImage([800, 600], (imageDataUrl) => {
              if (imageDataUrl) {
                const arr = imageDataUrl.split(',');
                const image = arr[arr.length - 1];
                this.profileService.saveProfileMapImagePreview(profile.id, image).subscribe((_) => {});
              }
            });
          }
        });
      }
    });
  }

  public callBottomSheetClosing() {
    this.bottomSheetClosingSubject.next(null);
  }

  public closeMainMenu() {
    this.mainMenuCloseSubject.next(null);
  }

  public async buildSelectObjectTypeAndGeoDefIdList(
    mapInfoObjectTypeCandidates: ObjectTypeWithGeoDefsCandidate[],
  ): Promise<SelectOtAndGidListItem[]> {
    return this.lib.buildSelectObjectTypeAndGeoDefIdList(mapInfoObjectTypeCandidates);
  }

  public openSelectObjectTypeAndGeoDefDialog(
    objectTypeAndGeoDefIdListItems: SelectOtAndGidListItem[],
    continueIfOk: (info: CreateGeoObjectInfo) => void,
    continueIfCancelled: () => void = null,
  ) {
    this.lib.openSelectObjectTypeAndGeoDefDialog(objectTypeAndGeoDefIdListItems, continueIfOk, continueIfCancelled);
  }

  public openNewObjectDialog(objectInfo: DataEntity, identifier: string) {
    this.lib.openNewObjectDialog(objectInfo, identifier);
  }

  deleteObjects(entities: DataEntity[]) {
    const query = new PersistObjectsModel();
    query.Objects = [];
    if (entities) {
      const arr: TargetObjectData[] = [];
      query.Objects.push(arr);
      for (let entity of entities) {
        const data: TargetObjectData = {
          Mode: PersistMode.Delete,
          ObjectKey: {
            Id: entity.id,
            ObjectType: entity.objectType.toString(),
          },
          TargetColumns: [],
        };
        arr.push(data);
      }
    }
    this.coreData.executePersistObjectsQuery(query).subscribe(
      () => {
        this.map.refreshMapLayer(entities);
      },
      (err) => {
        this.error.publish(err);
      },
    );
  }

  private async moveNode(entity: DataEntity): Promise<void> {
    try {
      const designatedCoordinate = await this.data.queryDesignatedEntityForMovingNode(entity);

      let coordinate: any;
      if (designatedCoordinate && designatedCoordinate.length > 2) {
        coordinate = this.transformToFeatureProjection(designatedCoordinate);
      } else {
        coordinate = entity.feature?.getGeometry()?.getFirstCoordinate();
      }

      if (coordinate) {
        const newCoordinate = await this.map.moveToNewCoordinate(coordinate);
        if (newCoordinate) {
          if (newCoordinate.length == 2) {
            newCoordinate[2] = null;
          } else if (newCoordinate.length > 2 && !newCoordinate[2]) {
            newCoordinate[2] = coordinate[2];
          }

          await this.moveNodeCore(entity, newCoordinate);
        }
      }
    } catch (err) {
      this.error.publish(err);
    }
  }

  private transformToFeatureProjection(srcCoordinate: number[]): number[] {
    const extent = [srcCoordinate[0], srcCoordinate[1], srcCoordinate[0], srcCoordinate[1]];
    const transformed = this.map.getCurrentView().transformExtentFromDataProjection(extent);
    const retCoord = [transformed[0], transformed[1], srcCoordinate[2]];
    return retCoord;
  }

  private async moveNodeCore(entity: DataEntity, newCoordinate: number[]) {
    const moveNodeTask = await from(this.resAsync.then((_) => _.get('Processing-MoveNode')))
      .pipe(map((txt) => this.loadingScreenService.addNewTask(txt, true)))
      .toPromise();

    const query = new ExecuteMoveNodeDef();
    query.Id = entity.id;
    query.ObjectType = entity.objectType;
    if (newCoordinate) {
      try {
        const newCoordinateDef: CoordinateDef = new CoordinateDef();
        newCoordinateDef.X = newCoordinate[0];
        newCoordinateDef.Y = newCoordinate[1];
        newCoordinateDef.Z = newCoordinate.length > 2 ? newCoordinate[2] : null;
        query.NewCoordinate = newCoordinateDef;

        const result = await this.coreData.executeWwMoveNode(query).toPromise();
        const changedEntities: DataEntity[] = [];
        for (const o of result) {
          changedEntities.push(new DataEntity(o.ObjectType, o.Id));
        }
        await this.map.prepareObjectGraphics(changedEntities).toPromise();
        this.map.refreshMapLayer(changedEntities);
      } catch (err) {
        this.error.publish(err);
      } finally {
        if (moveNodeTask) {
          this.loadingScreenService.finishTask(moveNodeTask.id, true);
        }
      }
    }
  }

  public get canLogout(): Observable<boolean> {
    return this.userService.canLogout;
  }

  public getAbwSammlerForDropDown(): Promise<NetworkObjectInfo[]> {
    const objTypeWorkArea: string = 'AbwSammler';
    return this.getEdgeObjectsCore(objTypeWorkArea);
  }

  public getAbwEntwaesserungsgebietForDropDown(): Promise<NetworkObjectInfo[]> {
    const objTypeWorkArea: string = 'AbwEntwaesserungsgebiet';
    return this.getEdgeObjectsCore(objTypeWorkArea, 'Entwaesserungsgebiet');
  }

  public getWorkAreasForDropDown(): Promise<NetworkObjectInfo[]> {
    const objTypeWorkArea: string = 'Arbeitsbereich';
    return this.getEdgeObjectsCore(objTypeWorkArea);
  }

  public getEdgeObjectsForNetworkTracingDropDown(
    objectType: ObjectTypeInfoViewModel,
    includingConnectionPipes: boolean | undefined = null,
  ): Promise<NetworkObjectInfo[]> {
    const otName = this.applyObjectTypeFilter(objectType, includingConnectionPipes);
    return this.getEdgeObjectsCore(otName);
  }

  private getEdgeObjectsCore(
    objectTypeName: string,
    designationColumnName: string = 'Bezeichnung',
  ): Promise<NetworkObjectInfo[]> {
    const objectQuery = this.coreData.createSimpleQuery(
      DataService.MainObjectSpaceName,
      objectTypeName,
      'ParentId = $CurrentMunicipalityVariantId',
      [
        this.coreData.createQueryColumn('Id', 'Id'),
        this.coreData.createQueryColumn('ObjectType', 'ObjectType'),
        this.coreData.createQueryColumn(designationColumnName, 'Designation', QueryColumnSortOrder.Ascending),
      ],
    );

    return this.coreData
      .executeReadObjectsQuery(objectQuery)
      .pipe(
        map((results) => {
          return this.getResultsArray<NetworkObjectInfo>(results);
        }),
      )
      .toPromise();
  }

  private getResultsArray<T>(results: ArrayBuffer): T[] {
    if (results && Array.isArray(results)) {
      return results;
    } else {
      return [];
    }
  }
}
